import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useCaptcha } from "@/context/CaptchaContext";
import { toast } from "sonner";

export default function ListenerRegister() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { registerListener } = useAuth();
  const { executeRecaptcha, enabled: captchaEnabled } = useCaptcha();
  
  const token = searchParams.get("token");
  
  const [formData, setFormData] = useState({
    phone: "",
    password: "",
    confirmPassword: ""
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Token de invitación no válido");
      navigate("/");
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    setLoading(true);
    
    try {
      // Get captcha token if enabled
      let captchaToken = null;
      if (captchaEnabled && executeRecaptcha) {
        captchaToken = await executeRecaptcha("register_listener");
      }
      
      await registerListener({
        token,
        phone: formData.phone || null,
        password: formData.password,
        captcha_token: captchaToken
      });
      toast.success("¡Registro completado! Bienvenido/a a Apoyum");
      navigate("/listener");
    } catch (error) {
      console.error("Register error:", error);
      toast.error(error.response?.data?.detail || "Error al completar el registro");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return null;
  }

  return (
    <Layout showFooter={false}>
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-xl border border-stone-100 p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-serif font-bold text-foreground">Completa tu registro</h1>
              <p className="mt-2 text-stone-600">Únete como listener en Apoyum</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="phone">Teléfono (WhatsApp)</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+52 55 1234 5678"
                  className="mt-2"
                  data-testid="listener-register-phone"
                />
              </div>

              <div>
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Mínimo 6 caracteres"
                  required
                  className="mt-2"
                  data-testid="listener-register-password"
                />
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="Repite tu contraseña"
                  required
                  className="mt-2"
                  data-testid="listener-register-confirm-password"
                />
              </div>

              <Button
                type="submit"
                className="w-full btn-primary text-lg py-6"
                disabled={loading}
                data-testid="listener-register-submit"
              >
                {loading ? "Completando registro..." : "Completar registro"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
}
